import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { DprWeatherDetailGridData } from '@dpr-details/models/grid';
import { GridSync } from '@grid/models';
import { ConfirmDialogComponent } from '@shared/components';
import { StaticMessage } from '@shared/enums';
import { DprWeatherDetail, DprWeatherDetailsItem } from '@shared/models/dpr';
import { Weather } from '@shared/models/project-setup';
import { Constants, getStaticMessage } from '@shared/utils';

@Injectable()
export class DprWeatherGridService {
  constructor(private dialog: MatDialog) {}

  buildGridData(
    dprWeatherDetails: DprWeatherDetail[]
  ): DprWeatherDetailGridData[] {
    const result = [];

    dprWeatherDetails?.forEach((x) => {
      const weatherDetail = {
        id: x.id,
        dprWeatherId: x.dprWeatherId,
        time: x.time,
      };

      // pivot weather items to columns
      x.dprWeatherDetailsItems?.forEach((y) => {
        weatherDetail[y.weatherId] = y?.value ?? null;
      });

      result.push(weatherDetail);
    });

    return result;
  }

  validateChanges(
    changes: GridSync<DprWeatherDetailGridData>,
    labeling: { [key: string]: string }
  ): boolean {
    const entities = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ];

    for (const entity of entities) {
      const errorMessages = [];

      if (!entity.time) {
        errorMessages.push(
          getStaticMessage(
            StaticMessage.FieldMandatoryError,
            labeling?.timeFieldName
          )
        );
      }

      if (errorMessages.length) {
        this.dialog.open(ConfirmDialogComponent, {
          ...Constants.DialogOptions,
          data: {
            extra: labeling?.gridEntityName,
            title: getStaticMessage(StaticMessage.ValidationErrorTitle),
            message: errorMessages.join('<br>'),
            buttonLabel: getStaticMessage(StaticMessage.OkButton),
            hideCancelButton: true,
          },
        });

        return false;
      }
    }

    return true;
  }

  mapToGridSync(
    dprWeatherId: number | string,
    changes: GridSync<DprWeatherDetailGridData>,
    dprWeatherDetails: DprWeatherDetail[],
    weatherList: Weather[]
  ): GridSync<DprWeatherDetail> {
    const result = {
      addedList: [],
      updatedList: [],
      deletedIds: changes?.deletedIds ?? [],
    };

    result.addedList =
      changes?.addedList?.map((data) => {
        const dprWeatherDetailsItems = this.buildDetailsItems(
          data,
          weatherList
        );

        const dprWeatherDetail: DprWeatherDetail = {
          id: data.id,
          dprWeatherId,
          time: data.time,
          dprWeatherDetailsItems,
        };

        return dprWeatherDetail;
      }) ?? [];

    result.updatedList =
      changes?.updatedList?.map((x) => {
        const existingDprWeatherDetail = dprWeatherDetails.find(
          (y) => y.id === x.id
        );

        const dprWeatherDetailsItems = this.buildDetailsItems(
          x,
          weatherList,
          existingDprWeatherDetail
        );

        const dprWeatherDetail: DprWeatherDetail = {
          id: x.id,
          dprWeatherId,
          time: x.time,
          dprWeatherDetailsItems,
        };

        return dprWeatherDetail;
      }) ?? [];

    return result;
  }

  private buildDetailsItems(
    row: DprWeatherDetailGridData,
    weatherList: Weather[],
    existingDprWeatherDetail?: DprWeatherDetail
  ): DprWeatherDetailsItem[] {
    const dprWeatherDetailsItems: DprWeatherDetailsItem[] = [];

    for (const weather of weatherList) {
      const existingDprWeatherDetailItem =
        existingDprWeatherDetail?.dprWeatherDetailsItems?.find(
          (x) => x.weatherId === weather.id
        );

      dprWeatherDetailsItems.push({
        ...existingDprWeatherDetailItem,
        weatherId: weather.id,
        value: row[weather.id] ?? null,
      });
    }

    return dprWeatherDetailsItems;
  }
}
